/**
 * F28 (Wave 2C-ii) — "Remove email suppression" modal.
 *
 * Composes Modal + FormField + Textarea (mandatory reason ≥ 10 chars per
 * `RemoveEmailSuppressionInputSchema`). Renders a warning Banner so the
 * admin knows bounces may re-suppress the address automatically on the
 * next send.
 *
 * Gated by `email_suppressions:write` (super_admin + support only) — the
 * parent page hides the row's Remove action for content_moderator.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, TextInput, Textarea } from '@bravemove/ui-web';
import {
  RemoveEmailSuppressionInputSchema,
  type RemoveEmailSuppressionInput,
} from '@bravemove/types';

export interface RemoveSuppressionDialogProps {
  readonly open: boolean;
  readonly emailAddress: string;
  readonly onConfirm: (input: RemoveEmailSuppressionInput) => Promise<void>;
  readonly onClose: () => void;
}

export function RemoveSuppressionDialog({
  open,
  emailAddress,
  onConfirm,
  onClose,
}: RemoveSuppressionDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setServerError(null);
    setSubmitting(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const validation = useMemo(
    () => RemoveEmailSuppressionInputSchema.safeParse({ reason: reason.trim() }),
    [reason],
  );

  const disableSubmit = submitting || !validation.success;

  const submit = async () => {
    if (!validation.success) return;
    setServerError(null);
    setSubmitting(true);
    try {
      await onConfirm(validation.data);
      onClose();
    } catch {
      setServerError(
        t('admin:email_suppressions.remove.error_generic', {
          defaultValue: 'Could not remove suppression. Please try again.',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:email_suppressions.remove.dialog_title', {
        defaultValue: 'Remove email suppression',
      })}
      description={t('admin:email_suppressions.remove.dialog_description', {
        defaultValue:
          'BraveMove will resume sending email to this address. Bounces will re-suppress automatically.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="remove-suppression-cancel"
          >
            {t('admin:email_suppressions.remove.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="remove-suppression-confirm"
          >
            {t('admin:email_suppressions.remove.submit', { defaultValue: 'Remove suppression' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="remove-suppression-dialog"
        data-email-address={emailAddress}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div data-testid="remove-suppression-warning">
          <Banner tone="warning">
            {t('admin:email_suppressions.remove.warning', {
              defaultValue:
                'This action is audit-logged. If this address had a hard bounce, BraveMove may suppress it again on the next send.',
            })}
          </Banner>
        </div>

        <FormField
          label={t('admin:email_suppressions.remove.email_label', {
            defaultValue: 'Email address',
          })}
        >
          <TextInput
            value={emailAddress}
            readOnly
            data-testid="remove-suppression-email"
            aria-label={t('admin:email_suppressions.remove.email_label', {
              defaultValue: 'Email address',
            })}
          />
        </FormField>

        <FormField
          label={t('admin:email_suppressions.remove.reason_label', {
            defaultValue: 'Reason for removal',
          })}
          helper={t('admin:email_suppressions.remove.reason_helper', {
            defaultValue: 'Required. 10–500 characters. Visible in the audit log.',
          })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            data-testid="remove-suppression-reason"
            aria-label={t('admin:email_suppressions.remove.reason_label', {
              defaultValue: 'Reason for removal',
            })}
          />
        </FormField>

        {serverError ? (
          <div data-testid="remove-suppression-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default RemoveSuppressionDialog;
